import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../GameDetailsOverlay/GameDetailsOverlay.css';
import { formatText } from '../../utils/typografUtils';

export default function GameDetailsOverlayMy({ game, onClose = () => { }, onPlay = () => { } }) {
  const [busy, setBusy] = useState(false);
  const [showPaymentRedirect, setShowPaymentRedirect] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Функция шаринга
  const handleShare = useCallback(async () => {
    const shareUrl = `${window.location.origin}/game/share/${game.slug}`;
    const shareData = {
      title: game.title,
      text: game.subtitle || (game.description ? game.description.slice(0, 100) + '...' : ''),
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      }
    } catch (err) {
      // Пользователь отменил шаринг или произошла ошибка
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        // Fallback на копирование
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);
        } catch {
          console.error('Clipboard error');
        }
      }
    }
  }, [game]);

  // Функция покупки полной версии (должна быть до раннего return)
  const buyDigital = useCallback(async () => {
    if (!game?.priceCents || game.priceCents <= 0) return;

    setBusy(true);
    setShowPaymentRedirect(true);

    try {
      const { createPayment } = await import('../../api/payments');

      // Получаем поля для формы от edge-функции
      const formData = await createPayment({
        gameId: game.id
      });

      // Создаём HTML форму
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://pro.selfwork.ru/merchant/v1/init';

      // Добавляем скрытые поля
      const fields = {
        'order_id': formData.order_id,
        'amount': formData.amount,
        'signature': formData.signature
      };

      // Добавляем info поля (товары)
      if (formData.info && Array.isArray(formData.info)) {
        formData.info.forEach((item, index) => {
          fields[`info[${index}][name]`] = item.name;
          fields[`info[${index}][quantity]`] = item.quantity;
          fields[`info[${index}][amount]`] = item.amount;
        });
      }

      // Создаём input элементы
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      // Добавляем форму в DOM и отправляем
      document.body.appendChild(form);
      form.submit();

    } catch (e) {
      console.error('buyDigital error:', e);
      alert(e.message || 'Не удалось оформить покупку');
      setShowPaymentRedirect(false);
      setBusy(false);
    }
    // НЕ сбрасываем busy, так как произойдёт редирект
  }, [game]);

  if (!game) return null;

  // Определяем, куплена ли игра полностью
  const isPurchased = game.accessVia === 'purchase';
  const isTrial = game.accessVia === 'trial';

  // Форматируем цену
  const formatPrice = (priceCents, currency = 'RUB') => {
    if (!priceCents || priceCents <= 0) return '';
    const symbol = currency === 'RUB' ? '₽' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
    const price = Math.round(priceCents / 100).toLocaleString('ru-RU');
    return `${price} ${symbol}`;
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={`Описание моей игры ${game.title}`}>
      <div
        className="overlay-bg overlay-bg--current"
        aria-hidden="true"
        style={{ backgroundImage: `url(${game.coverUrl})` }}
      />
      <div className="overlay-content">
        <div className="overlay-body">
          <div className="overlay-scroll">
            <div className="game-hero-wrap">
              <img className="game-hero" src={game.coverUrl} alt={game.title} />
            </div>
            <h2 className="overlay-title">{formatText(game.title)}</h2>
            {game.subtitle && <div className="overlay-subtitle">{formatText(game.subtitle)}</div>}

            {/* Секция бейджей */}
            <div className="overlay-badges">
              {/* Бейдж количества карт */}
              {isTrial ? (
                <span className="overlay-badge overlay-badge--green">
                  <img src="/icons/cards-green.svg" alt="" className="overlay-badge-icon" />
                  <span>7</span>
                  <span className="overlay-badge-muted">/ {game.cardCount || 70}</span>
                </span>
              ) : isPurchased ? (
                <span className="overlay-badge overlay-badge--orange">
                  <img src="/icons/cards-orange.svg" alt="" className="overlay-badge-icon" />
                  <span>{game.cardCount || 70}</span>
                </span>
              ) : null}

              {/* Бейдж покупки / статуса */}
              {isTrial ? (
                <span
                  className="overlay-badge overlay-badge--orange overlay-badge--clickable"
                  onClick={buyDigital}
                  style={{ opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : 'auto' }}
                >
                  Полный набор {formatPrice(game.priceCents, game.currency)}
                  <span className="overlay-badge-old-price">790 ₽</span>
                  <img src="/icons/forward-orange.svg" alt="" className="overlay-badge-arrow" />
                </span>
              ) : isPurchased ? (
                <span className="overlay-badge overlay-badge--orange">
                  Приобретен полный набор
                  <img src="/icons/сheck-orange.svg" alt="" className="overlay-badge-check" />
                </span>
              ) : null}

              {/* Бейдж OZON */}
              {game.ozonLink && (
                <a
                  href={game.ozonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="overlay-badge overlay-badge--ozon overlay-badge--clickable"
                >
                  Печатная версия на OZON
                  <img src="/icons/forward-ozon.svg" alt="" className="overlay-badge-arrow" />
                </a>
              )}
            </div>

            {game.description && (
              <p className="overlay-text">{formatText(game.description)}</p>
            )}
          </div>
        </div>
      </div>

      <div className="tabbar-wrap">
        <div className="tabbar single" onClick={onClose}>
          <img src="/icons/back-arrow.svg" alt="Закрыть" />
        </div>

        <div className="tabbar single" onClick={handleShare}>
          <img src="/icons/share.svg" alt="Поделиться" />
        </div>

        <button className="tab-btn-accent-large" aria-label="Играть" onClick={onPlay}>
          <span className="tab-btn-accent-large-icon">
            <img src="/icons/get-demo.svg" alt="" aria-hidden="true" />
          </span>
          Играть
        </button>
      </div>

      {/* Оверлей перенаправления на оплату */}
      {showPaymentRedirect && (
        <div className="game-options-overlay">
          <div className="game-options-menu">
            <div className="game-options-success menu-content">
              <div className="success-anim">
                <div className="payment-spinner"></div>
              </div>
              <div className="success-message">
                Переходим на страницу оплаты
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            className="share-toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            Ссылка скопирована
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
